<template>

  <calendar-toolbar
    v-model="calendarDate"
    :calendar="calendarRef"
    class="mb-8"
    :disabled="false"
  >

    <v-btn
      prepend-icon="$edit"
      class="ml-4"
      :disabled="!location || false"
      @click="openUpdateInventoryModal()"
    >
      <span class="d-lg-none">{{ $t('actions.update_inventory_abbr') }}</span>
      <span class="d-none d-lg-inline">{{ $t('actions.update_inventory') }}</span>
    </v-btn>

    <v-btn-toggle v-model="calendarType" mandatory class="ml-4">
      <v-btn value="day">
        <span class="d-lg-none">D</span>
        <span class="d-none d-lg-inline">{{ $t('titles.day') }}</span>
      </v-btn>
      <v-btn value="month">
        <span class="d-lg-none">{{ $t('titles.month_abbr') }}</span>
        <span class="d-none d-lg-inline">{{ $t('titles.month') }}</span>
      </v-btn>
    </v-btn-toggle>

  </calendar-toolbar>

  <calendar-with-sidebar>

    <template v-slot:sidebar>

      <div class="d-flex d-lg-block">

        <sidebar-datepicker
          v-model="calendarDate"
          class="mr-8 mr-lg-0 mb-lg-8"
        />

        <item-filter
          v-model="selectedLocationProductIds"
          :location-products="locationProducts"
          :loading="loadingLocationProducts"
          :has-on-demand-lps="locationHasOnDemandLps"
          :has-parcs-lps="locationHasParcsLps"
        />

      </div>

    </template>

    <normalized-calendar
      ref="calendar"
      first-day-of-week="1"
      v-model="calendarDate"
      :type="calendarType"
      :events="filteredCalendarItems"
      @click:event="openItemContextMenu"
      :loading="loadingDayInventory || loadingMonthInventory"
      :event-overlap-mode="calendarType === 'day' ? dayLayoutFn : 'column'"
    >

      <template v-slot:event="{ event }">

        <month-normal-bar v-if="event.type === 'month_normal'" :item="event" @dblclick="openUpdateInventoryModal(event)" />
        <month-event-bar v-if="event.type === 'month_event'" :item="event" @dblclick="openUpdateInventoryModal(event)" />
        <month-parcs-bar v-if="event.type === 'month_parcs'" :item="event" />
        <month-on-demand-bar v-if="event.type === 'month_on_demand'" :item="event" />
        <day-summary-bar v-if="event.type === 'day_summary'" :item="event" />
        <day-summary-with-parcs-bar v-if="event.type === 'day_summary_with_parcs'" :item="event" />
        <day-normal-bar v-if="event.type === 'day_normal'" :item="event" />
        <day-parcs-bar v-if="event.type === 'day_parcs'" :item="event" />
        <day-on-demand-bar v-if="event.type === 'day_on_demand'" :item="event" />

      </template>

    </normalized-calendar>

    <v-menu
      v-model="contextMenuOpen"
      :activator="contextMenuActivator"
      location="left"
      @after-leave="contextMenuActivator = undefined"
      width="300"
    >

      <template v-if="contextMenuItem">
        <!-- @vue-ignore -->
        <month-normal-popup v-if="contextMenuItem.type === 'month_normal'" :item="contextMenuItem" @edit="openUpdateInventoryModal(contextMenuItem)"  @view-breakdown="openBreakdownModal(contextMenuItem)" />
        <!-- @vue-ignore -->
        <month-event-popup v-if="contextMenuItem.type === 'month_event'" :item="contextMenuItem" @edit="openUpdateInventoryModal(contextMenuItem)" @view-breakdown="openBreakdownModal(contextMenuItem)" />
        <!-- @vue-ignore -->
        <day-summary-popup v-if="contextMenuItem.type === 'day_summary'"  :item="contextMenuItem" />
        <!-- @vue-ignore -->
        <day-summary-with-parcs-popup v-if="contextMenuItem.type === 'day_summary_with_parcs'"  :item="contextMenuItem" />
        <!-- @vue-ignore -->
        <day-normal-popup v-if="contextMenuItem.type === 'day_normal'" :item="contextMenuItem" />
      </template>

    </v-menu>

  </calendar-with-sidebar>

</template>

<script setup lang="ts">

import { computed, onBeforeMount, ref, toRef, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import { useTitle } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

// components
import CalendarToolbar from '../../../CalendarToolbar.vue'
import CalendarWithSidebar from '../../../CalendarWithSidebar.vue'
import NormalizedCalendar from '../../../NormalizedCalendar.vue'
import SidebarDatepicker from '../../../SidebarDatepicker.vue'
import ItemFilter from './ItemFilter.vue'
import MonthNormalBar from './bars/month/MonthNormalBar.vue'
import MonthEventBar from './bars/month/MonthEventBar.vue'
import MonthOnDemandBar from './bars/month/MonthOnDemandBar.vue'
import MonthParcsBar from './bars/month/MonthParcsBar.vue'
import DaySummaryBar from './bars/day/DaySummaryBar.vue'
import DaySummaryWithParcsBar from './bars/day/DaySummaryWithParcsBar.vue'
import DayNormalBar from './bars/day/DayNormalBar.vue'
import DayParcsBar from './bars/day/DayParcsBar.vue'
import DayOnDemandBar from './bars/day/DayOnDemandBar.vue'
// @ts-ignore
import UpdateInventoryModal from '../modals/UpdateInventoryModal.vue'
// @ts-ignore
import BreakdownModal from '../modals/BreakdownModal.vue'

// models
import Location from '../../../../models/Location'

// misc
import { Temporal } from '@js-temporal/polyfill'
import { useCalendarItems } from './composables/useCalendarItems'
import { useDayLayout } from './composables/useDayLayout'
import { useLocationData } from './composables/useLocationData'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import type {
  AbstractInventoryCalendarItem,
  MonthNormalInventoryCalendarItem,
  MonthEventInventoryCalendarItem,
} from './calendarItemTypes'

import MonthNormalPopup from './popups/MonthNormalPopup.vue'
import MonthEventPopup from './popups/MonthEventPopup.vue'
import DayNormalPopup from './popups/DayNormalPopup.vue'
import DaySummaryPopup from './popups/DaySummaryPopup.vue'
import DaySummaryWithParcsPopup from './popups/DaySummaryWithParcsPopup.vue'
import type { DateRange } from '../../../../types/misc'

/**
 * Props
 */

interface Props {
  location: null | Location // TODO: note which relations (if any) are assumed to be loaded
}

const props = defineProps<Props>()

const route = useRoute()

/**
 * Calendar component
 */

const calendarRef = useTemplateRef<NormalizedCalendarType>('calendar')
const calendarDate = ref<Temporal.PlainDate>(Temporal.Now.plainDateISO())
const calendarType = ref<'day' | 'month'>('day')

onBeforeMount(() => {

  if ('date' in route.query && !Array.isArray(route.query.date) && route.query.date !== null) {
    try {
      calendarDate.value = Temporal.PlainDate.from(route.query.date)
    } catch (e) {}
  } else if ('month' in route.query) {
    let queryDate
    try {
      queryDate = Temporal.PlainDate.from(`${route.query.month}-01`)
    } catch (e) {
      console.warn('"month" parameter in querystring is malformed; should be yyyy-mm')
      return
    }
    calendarDate.value = queryDate
    calendarType.value = 'month'
  }

})

const monthRange = computed<null | DateRange>(() => {

  const start = calendarRef.value?.monthStart ?? null
  const end = calendarRef.value?.monthEnd ?? null

  if (!start || !end) {
    return null
  }

  return {start, end}

})

/**
 * Calendar items
 */

const calendarItems = computed<AbstractInventoryCalendarItem[]>(() => {

  switch (calendarType.value) {
    case 'day':
      return dayCalendarItems.value
    case 'month':
      return monthCalendarItems.value
    default:
      return []
  }

})

const filteredCalendarItems = computed<AbstractInventoryCalendarItem[]>(() => {

  const showParcs = selectedLocationProductIds.value.includes('parcs')
  const showOnDemand = selectedLocationProductIds.value.includes('on_demand')

  return calendarItems.value.filter(item => {

    if (item.type === 'day_summary') {
      return true
    }

    if (showParcs && ['day_summary_with_parcs', 'day_parcs', 'month_parcs'].includes(item.type)) {
      return true
    }

    if (showOnDemand && ['day_on_demand', 'month_on_demand'].includes(item.type)) {
      return true
    }

    if (['day_normal', 'month_normal', 'month_event'].includes(item.type) && selectedLocationProductIds.value.includes(item.locationProduct.ID)) {
      return true
    }

    return false

  })

})

/**
 *
 */

const locationHasParcsLps = computed<boolean>(() => {
  return locationProducts.value.some(lp => lp.product.verticalType === 'parcs')
})

const locationHasOnDemandLps = computed<boolean>(() => {
  return locationProducts.value.some(lp => lp.product.verticalType === 'on_demand')
})

/**
 * Composables and such
 */

type NormalizedCalendarType = InstanceType<typeof NormalizedCalendar>

const { t } = useI18n()

const title = useTitle()
title.value = t('titles.universal_inventory')

const {
  locationProducts,
  loadingLocationProducts,
  dayInventory,
  loadDayInventory,
  loadingDayInventory,
  monthInventory,
  loadMonthInventory,
  loadingMonthInventory,
} = useLocationData(toRef(() => props.location), calendarType, calendarDate, monthRange)

const { dayCalendarItems, monthCalendarItems } = useCalendarItems(dayInventory, monthInventory, locationProducts, locationHasOnDemandLps, locationHasParcsLps)

const { dayLayoutFn } = useDayLayout(locationProducts)

/**
 * Location Products filter
 */

const selectedLocationProductIds = ref<(number | string)[]>([])

/**
 * Context menu
 * Based on: https://vuetifyjs.com/en/components/calendars/#click
 */

const contextMenuOpen = ref<boolean>(false)
const contextMenuItem = ref<null | AbstractInventoryCalendarItem>(null)
const contextMenuActivator = ref<undefined | HTMLElement>()

const itemTypesWithPopups = ['day_summary', 'day_summary_with_parcs', 'day_normal', 'month_normal', 'month_event']

function openItemContextMenu(nativeEvent: Event, { event }: {event: AbstractInventoryCalendarItem}) {

  if (!nativeEvent.target || !(nativeEvent.target instanceof HTMLElement)) {
    // we can't do anything with this (this shouldn't happen anyway)
    return
  }

  if (!itemTypesWithPopups.includes(event.type)) {
    return
  }

  const open = () => {
    contextMenuItem.value = event
    contextMenuActivator.value = nativeEvent.target as HTMLElement
    requestAnimationFrame(() => requestAnimationFrame(() => contextMenuOpen.value = true))
  }

  if (contextMenuOpen.value) {
    contextMenuOpen.value = false
    requestAnimationFrame(() => requestAnimationFrame(() => open()))
  } else {
    open()
  }

  nativeEvent.stopPropagation()

}

/**
 * Modals
 */

function openUpdateInventoryModal(item: null | MonthNormalInventoryCalendarItem | MonthEventInventoryCalendarItem = null) {

  if (!props.location) {
    return
  }

  openDialog(UpdateInventoryModal, {
    locationID: props.location.ID,
    verticalType: item ? item.locationProduct.product.verticalType : null,
    startDate: item ? Temporal.PlainDate.from(item.start) : null,
    endDate: item ? Temporal.PlainDate.from(item.end) : null,
    eventID: item && item.type === 'month_event' ? item.eventId : null,
  }).then((updated: boolean) => {
    if (updated && props.location) {
      if (calendarType.value === 'day') {
        loadDayInventory(props.location.ID)
      } else if (calendarType.value === 'month') {
        loadMonthInventory(props.location.ID)
      }
    }
  })

}

function openBreakdownModal(item: MonthNormalInventoryCalendarItem | MonthEventInventoryCalendarItem) {

  if (!props.location) {
    return
  }

  openDialog(BreakdownModal, {
    locationID: props.location.ID,
    locationProductID: item.locationProduct.ID,
    date: new Date(item.start),
  })
}

</script>

<style>

.v-calendar.v-calendar-daily .v-event-timed {
  border: 0 !important;
  background-color: transparent !important;
  padding: 0 !important;
}

</style>
